import { SmartConfirm } from '@/components/ui/SmartConfirm';
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Layers,
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  Copy,
  Building2,
} from 'lucide-react';
import { usePlybooks, useDeletePlybook, useApprovePlybook } from '@/hooks/usePlybooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { PlybookFormDialog } from './PlybookFormDialog';
import { PLYBOOK_STATUS_LABELS, PLYBOOK_STATUS_COLORS } from '@/types/plybook';
import type { Plybook } from '@/types/plybook';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PlybookListProps {
  onSelect?: (plybook: Plybook) => void;
  productModelId?: string;
  moldId?: string;
  onPlybookCreated?: (plybookId: string) => void;
  defaultCustomerId?: string;
  defaultProjectId?: string;
}

export function PlybookList({ onSelect, productModelId, moldId, onPlybookCreated, defaultCustomerId, defaultProjectId }: PlybookListProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingPlybook, setEditingPlybook] = useState<Plybook | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; plybook?: Plybook }>({
    open: false,
  });
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuId]);

  // Load customers for filter
  const { data: customers } = useQuery({
    queryKey: ['customers-select'],
    queryFn: async () => {
      const { data, error } = await api
        .from('customers')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const { data: plybooks, isLoading } = usePlybooks({
    productModelId,
    moldId,
    customerId: customerFilter !== 'all' ? customerFilter : undefined,
    status: statusFilter,
    search: search || undefined,
  });

  const deleteMutation = useDeletePlybook();
  const approveMutation = useApprovePlybook();

  const handleEdit = (plybook: Plybook) => {
    setEditingPlybook(plybook);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (deleteDialog.plybook) {
      await deleteMutation.mutateAsync(deleteDialog.plybook.id);
      setDeleteDialog({ open: false });
    }
  };

  const handleApprove = async (plybook: Plybook) => {
    await approveMutation.mutateAsync(plybook.id);
  };

  return (
    <div className="space-y-4">
      {/* Header & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar plybook..."
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="approved">Aprobado</SelectItem>
              <SelectItem value="obsolete">Obsoleto</SelectItem>
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los clientes</SelectItem>
              {customers?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={() => { setEditingPlybook(null); setFormOpen(true); }} className="gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Plybook
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 h-40" />
            </Card>
          ))}
        </div>
      ) : plybooks?.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layers className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No hay plybooks</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setFormOpen(true)}
            >
              Crear primer plybook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plybooks?.map((plybook) => (
            <Card
              key={plybook.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onSelect?.(plybook)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold line-clamp-1">{plybook.name}</h3>
                      {plybook.code && (
                        <p className="text-xs text-muted-foreground">{plybook.code}</p>
                      )}
                    </div>
                  </div>

                  <div className="relative" ref={openMenuId === plybook.id ? menuRef : null}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === plybook.id ? null : plybook.id);
                      }}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                    {openMenuId === plybook.id && (
                      <div className="absolute right-0 top-full mt-1 bg-popover border rounded-md shadow-lg z-50 py-1 min-w-[160px]">
                        <button
                          className="w-full px-3 py-2 text-sm text-left hover:bg-accent flex items-center gap-2"
                          onClick={(e) => { e.stopPropagation(); onSelect?.(plybook); setOpenMenuId(null); }}
                        >
                          <Eye className="w-4 h-4" /> Ver detalle
                        </button>
                        <button
                          className="w-full px-3 py-2 text-sm text-left hover:bg-accent flex items-center gap-2"
                          onClick={(e) => { e.stopPropagation(); handleEdit(plybook); setOpenMenuId(null); }}
                        >
                          <Edit className="w-4 h-4" /> Editar
                        </button>
                        {plybook.status === 'draft' && (
                          <button
                            className="w-full px-3 py-2 text-sm text-left hover:bg-accent flex items-center gap-2"
                            onClick={(e) => { e.stopPropagation(); handleApprove(plybook); setOpenMenuId(null); }}
                          >
                            <CheckCircle className="w-4 h-4" /> Aprobar
                          </button>
                        )}
                        <button
                          className="w-full px-3 py-2 text-sm text-left hover:bg-accent flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Copy className="w-4 h-4" /> Duplicar
                        </button>
                        <button
                          className="w-full px-3 py-2 text-sm text-left hover:bg-accent flex items-center gap-2 text-destructive"
                          onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, plybook }); setOpenMenuId(null); }}
                        >
                          <Trash2 className="w-4 h-4" /> Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={PLYBOOK_STATUS_COLORS[plybook.status]}>
                      {PLYBOOK_STATUS_LABELS[plybook.status]}
                    </Badge>
                    <Badge variant="outline">v{plybook.version}</Badge>
                    <Badge variant="secondary">{plybook.total_plies} capas</Badge>
                  </div>

                  {plybook.customer && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {plybook.customer.name}
                    </p>
                  )}

                  {plybook.mold && (
                    <p className="text-sm text-muted-foreground">
                      Molde: {plybook.mold.name}
                    </p>
                  )}

                  {plybook.total_weight_grams && (
                    <p className="text-xs text-muted-foreground">
                      Peso total: {plybook.total_weight_grams.toFixed(0)}g
                    </p>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {format(new Date(plybook.created_at), "d MMM yyyy", { locale: es })}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <PlybookFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        plybook={editingPlybook}
        defaultCustomerId={defaultCustomerId}
        defaultProjectId={defaultProjectId}
        onSuccess={(created) => {
          if (created && onPlybookCreated) {
            onPlybookCreated(created.id);
          }
        }}
      />

      {/* Delete Confirmation */}
      <SmartConfirm
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        onConfirm={handleDelete}
        type="delete"
        title="Eliminar Plybook"
        description="Esta accion eliminara el plybook permanentemente. Esta accion no se puede deshacer."
        affectedItems={deleteDialog.plybook ? [
          { label: "Nombre", value: deleteDialog.plybook.name },
          { label: "Capas", value: deleteDialog.plybook.plies?.length || 0 }
        ] : undefined}
        confirmText="Eliminar"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
